import { createWalletClient, http, PublicClient, WalletClient } from 'viem';
import { mainnet } from 'viem/chains';

import { IRaffleState } from 'modules/prize-box-page/prize-box-page.types';

import { SUPPORTED_CHAIN_IDS } from 'shared/config/config.types';
import { MovesEnum } from 'shared/constants/shared-enums';

import { MOCK_PLAYER_A_ADDRESS, MOCKED_BONUS_POINTS_VALUE } from './mock-rooms';

export const DEFAULT_MOCK_ADDRESS = MOCK_PLAYER_A_ADDRESS as `0x${string}`;

export const MOCK_ALLOWANCE = 1000000000000000000000000n;

export const PRIZE_CRATE_MOCK_DATA: IRaffleState & { attempts: number } = {
  poolUsdt: 1000,
  recoveryAmount: 300,
  attempts: 3,
  extraCredits: 0,
  needsRestore: false,
  contractReadable: true,
  currentSlotBalance: 50,
  thresholdCents: 100,
  spendLimit: 200,
  slotDurationSec: 3600,
  lastSlotEndTime: Date.now() + 3600 * 1000,
  windowStart: Date.now(),
  quotaResetAt: Date.now() - 2 * 60 * 60 * 1000,
};

export const MOCKED_NFT_INITIAL_STATE = {
  nftBalances: {
    bronze: {
      [MovesEnum.Rock]: { count: 0, canForge: false, canMint: false, openTimeOut: 200 },
      [MovesEnum.Paper]: { count: 0, canForge: false, canMint: true, openTimeOut: null },
      [MovesEnum.Scissors]: { count: 1, canForge: false, canMint: true, openTimeOut: 600 },
    },
    silver: {
      [MovesEnum.Rock]: { count: 2, canForge: true, canMint: false, openTimeOut: 400 },
      [MovesEnum.Paper]: { count: 0, canForge: false, canMint: false, openTimeOut: null },
      [MovesEnum.Scissors]: { count: 0, canForge: false, canMint: false, openTimeOut: null },
    },
    gold: {
      [MovesEnum.Rock]: { count: 0, canForge: false, canMint: false, openTimeOut: null },
      [MovesEnum.Paper]: { count: 1, canForge: true, canMint: true, openTimeOut: null },
      [MovesEnum.Scissors]: { count: 0, canForge: false, canMint: false, openTimeOut: null },
    },
    platinum: {
      [MovesEnum.Rock]: { count: 0, canForge: false, canMint: false, openTimeOut: null },
      [MovesEnum.Paper]: { count: 0, canForge: false, canMint: false, openTimeOut: null },
      [MovesEnum.Scissors]: { count: 1, canForge: true, canMint: true, openTimeOut: null },
    },
    diamond: {
      [MovesEnum.Rock]: { count: 1, canForge: true, canMint: true, openTimeOut: null },
      [MovesEnum.Paper]: { count: 0, canForge: false, canMint: false, openTimeOut: null },
      [MovesEnum.Scissors]: { count: 0, canForge: false, canMint: false, openTimeOut: null },
    },
    none: {
      [MovesEnum.Rock]: { count: 0, canForge: false, canMint: false, openTimeOut: null },
      [MovesEnum.Paper]: { count: 1, canForge: true, canMint: true, openTimeOut: null },
      [MovesEnum.Scissors]: { count: 0, canForge: false, canMint: false, openTimeOut: null },
    },
    epic: {
      [MovesEnum.Rock]: { count: 0, canForge: false, canMint: false, openTimeOut: null },
      [MovesEnum.Paper]: { count: 1, canForge: true, canMint: true, openTimeOut: null },
      [MovesEnum.Scissors]: { count: 0, canForge: false, canMint: false, openTimeOut: null },
    },
    legendary: {
      [MovesEnum.Rock]: { count: 0, canForge: false, canMint: false, openTimeOut: null },
      [MovesEnum.Paper]: { count: 0, canForge: false, canMint: false, openTimeOut: null },
      [MovesEnum.Scissors]: { count: 1, canForge: true, canMint: true, openTimeOut: null },
    },
  },
  pointBalance: MOCKED_BONUS_POINTS_VALUE,
  bestTierPerMove: {
    [MovesEnum.Rock]: 15,
    [MovesEnum.Paper]: 18,
    [MovesEnum.Scissors]: 21,
  },
  bestOpponentTierPerMove: {
    [MovesEnum.Rock]: 1,
    [MovesEnum.Paper]: 2,
    [MovesEnum.Scissors]: 3,
  },
  snapshotPoints: 120n,
  isMinting: false,
  minted: false,
  isForging: false,
  isForgeSuccess: null,
  mintError: null,
  forgeError: null,
};

export const MOCK_CHAIN = {
  id: SUPPORTED_CHAIN_IDS.ARBITRUM,
  name: 'Arbitrum One',
  network: 'arbitrum',
  rpcUrls: {
    default: { http: ['https://arb1.arbitrum.io/rpc'] },
  },
  nativeCurrency: {
    name: 'Ether',
    symbol: 'ETH',
    decimals: 18,
  },
  blockExplorers: {
    default: {
      name: 'Arbitrum Explorer',
      url: 'https://arbiscan.io',
    },
  },
};

export const MOCK_ACCOUNT = {
  address: MOCK_PLAYER_A_ADDRESS,
  chainId: MOCK_CHAIN.id,
  isConnected: true,
  status: 'connected',
  isConnecting: false,
  chain: MOCK_CHAIN,
  isReconnecting: false,
  isDisconnected: false,
  isError: false,
  isPrivyWallet: false,
  isIdle: false,
  isSuccess: true,
  isLoading: false,
  isPending: false,
};

export const MOCK_CONNECT = {
  connectAsync: async () => ({
    account: DEFAULT_MOCK_ADDRESS,
    chainId: MOCK_CHAIN.id,
  }),
  connectors: [
    {
      id: 'injected',
      name: 'Injected',
      iconUrl: '/icons/injected.svg',
      iconBackground: '#ffffff',
      ready: true,
      installed: true,
      type: 'injected',
    },
  ],
  isLoading: false,
  isError: false,
  isSuccess: true,
};

export const MOCK_BLOCK_NUMBER = {
  data: 12345678n,
  error: null,
  isLoading: false,
  refetch: async () => {},
};

export const MOCK_DISCONNECT = {
  disconnectAsync: async () => {
    console.log('Mocked disconnect');
    await new Promise((res) => setTimeout(res, 300));
  },
  disconnect: () => {
    console.log('Mocked disconnect (sync)');
  },
  isSuccess: true,
  isError: false,
  isLoading: false,
};

const mockTransport = http();

export const MOCK_PUBLIC_CLIENT = {
  chain: mainnet,
  transport: mockTransport,
  account: undefined,
  batch: undefined,
  cacheTime: 0,
  ccipRead: false,
  key: 'mock',
  name: 'mockPublicClient',
  pollingInterval: 1000,
  type: 'publicClient',
  uid: 'mock-uid',

  request: async (_args: any) => null,

  watchContractEvent: () => () => {},
  watchBlockNumber: () => () => {},
  watchEvent: () => () => {},

  extend: function <TClient extends PublicClient>(fn: (client: PublicClient) => TClient): TClient {
    return fn(this as any);
  },

  // Stubs for required PublicClient methods
  createAccessList: async () => null as any,
  createBlockFilter: async () => null as any,
  createPendingTransactionFilter: async () => null as any,
  estimateFeesPerGas: async () => null as any,
  estimateGas: async () => null as any,
  getBalance: async () => null as any,
  getBlock: async () => null as any,
  getBlockNumber: async () => null as any,
  getBlockTransactionCount: async () => null as any,
  getBytecode: async () => null as any,
  getChainId: async () => 1,
  getClientVersion: async () => 'mock',
  getCode: async () => null as any,
  getCoinbase: async () => null as any,
  getEnsAddress: async () => null as any,
  getEnsAvatar: async () => null as any,
  getEnsName: async () => null as any,
  getEnsResolver: async () => null as any,
  getEnsText: async () => null as any,
  getFeeHistory: async () => null as any,
  getFilterChanges: async () => null as any,
  getFilterLogs: async () => null as any,
  getGasPrice: async () => null as any,
  getLogs: async () => [],
  getProof: async () => null as any,
  getStorageAt: async () => null as any,
  getTransaction: async () => null as any,
  getTransactionConfirmations: async () => null as any,
  getTransactionCount: async () => null as any,
  getTransactionReceipt: async () => null as any,
  getTransactionTrace: async () => null as any,
  getUncleCount: async () => null as any,
  getUncle: async () => null as any,
  multicall: async () => null as any,
  readContract: async () => null as any,
  simulateContract: async () => null as any,
  waitForTransactionReceipt: async () => null as any,
} as unknown as PublicClient;

export const MOCK_WALLET_CLIENT = {
  ...(createWalletClient({
    account: {
      address: DEFAULT_MOCK_ADDRESS,
      type: 'json-rpc',
    },
    chain: mainnet,
    transport: mockTransport,
  }) as unknown as WalletClient),
  writeContract: async () => '0xmockedTxHash',
  sendTransaction: async () => '0xmockedTxHash',
  signMessage: async () => '0xMockSignature',
} as WalletClient;

export const MOCK_READ_CONTRACT = {
  balanceOf: {
    data: BigInt('100000000000000000000'), // 100 tokens
    isSuccess: true,
    isError: false,
    refetch: () => {},
  },
  decimals: {
    data: 18,
    isSuccess: true,
    isError: false,
    refetch: () => {},
  },
  allowance: {
    data: BigInt('1000000000000000000'), // 1 token
    isSuccess: true,
    isError: false,
    refetch: () => {},
  },
  defaultError: {
    data: null,
    isSuccess: false,
    isError: true,
    refetch: () => {},
  },
};

export const MOCK_SEND_TRANSACTION = {
  sendTransaction: async () => {
    console.log('Mocked sendTransaction');
    return { hash: '0xMOCKED_HASH' };
  },
  isLoading: false,
  isError: false,
  isSuccess: true,
  data: { hash: '0xMOCKED_HASH' },
};

export const MOCK_WRITE_CONTRACT = {
  writeContract: async () => {
    console.log('Mocked writeContract');
    return { hash: '0xMOCKED_HASH' };
  },
  isLoading: false,
  isError: false,
  isSuccess: true,
  data: { hash: '0xMOCKED_HASH' },
};

export const MOCK_SIGN_MESSAGE = {
  signMessage: async () => {
    console.log('Mocked signMessage');
    return '0xMOCKED_SIGNATURE';
  },
  isLoading: false,
  isError: false,
  isSuccess: true,
  data: '0xMOCKED_SIGNATURE',
};

export const MOCK_BALANCE = {
  balance: 50,
  chainBalances: {
    [SUPPORTED_CHAIN_IDS.ARBITRUM]: BigInt('100000000000000000000'), // 100 tokens
  },
  refetch: () => {},
};

export const MOCKED_SWITCH_CHAIN = {
  error: undefined,
  isLoading: false,
  chains: [
    {
      id: SUPPORTED_CHAIN_IDS.ARBITRUM,
      name: 'Arbitrum One',
      network: 'arbitrum',
      rpcUrls: {
        default: { http: ['https://arb1.arbitrum.io/rpc'] },
      },
      nativeCurrency: {
        name: 'Ether',
        symbol: 'ETH',
        decimals: 18,
      },
      blockExplorers: {
        default: {
          name: 'Arbitrum Explorer',
          url: 'https://arbiscan.io',
        },
      },
    },
  ],
  isError: false,
  isSuccess: true,
};

const MOCK_TRANSACTION_RECEIPT_DATA = {
  blockHash: '0xmockblockhash',
  blockNumber: BigInt(12345678),
  contractAddress: '0xMockedContract',
  cumulativeGasUsed: BigInt(21000),
  effectiveGasPrice: BigInt(1000000000),
  gasUsed: BigInt(21000),
  logs: [],
  logsBloom: '0x' + '0'.repeat(512),
  status: 'success',
  transactionHash: '0xMockedTxHash',
  transactionIndex: 0,
  type: '0x2',
  byzantium: true,
  confirmations: 1,
};

export const createMockTransactionReceiptResponse = (hash?: string) => {
  const data = {
    ...MOCK_TRANSACTION_RECEIPT_DATA,
    transactionHash: hash ?? MOCK_TRANSACTION_RECEIPT_DATA.transactionHash,
  };

  return {
    data,
    isSuccess: true,
    isError: false,
    isPending: false,
    isLoading: false,
    isLoadingError: false,
    isRefetchError: false,
    isPlaceholderData: false,
    status: 'success',
    error: null,
    fetchStatus: 'idle',
    failureCount: 0,
    failureReason: null,
    isFetched: true,
    isFetchedAfterMount: true,
    isFetching: false,
    isPaused: false,
    isRefetching: false,
    dataUpdatedAt: Date.now(),
    errorUpdatedAt: 0,
    queryKey: ['waitForTransactionReceipt', hash],
    refetch: () =>
      Promise.resolve({
        data,
      }),
  } as any;
};

export const MOCK_TOKEN_BALANCES: Record<string, bigint> = {
  '0xfd086bc7cd5c481dcc9c85ebe478a1c0b69fcbb9': 100000000n,
  '0x2f2a2543b76a4166549f7aab2e75bef0aefc5b0f': 250000000000000000000n,
};

// Persist across renders *and* HMR (Vite/Webpack) by stashing on globalThis.
export type MockGuards = {
  runsPerRound: Set<string>;
  inFlight: Map<string, Promise<void>>;
  lastStageByKey: Map<string, number>;
};

export const __MOCK_GUARDS__: MockGuards =
  ((globalThis as any).__ESPACE_MOCK_GUARDS__ as MockGuards) ??
  ((globalThis as any).__ESPACE_MOCK_GUARDS__ = {
    runsPerRound: new Set<string>(),
    inFlight: new Map<string, Promise<void>>(),
    lastStageByKey: new Map<string, number>(),
  });

export const mockRunKey = (roomId?: string | number | null, round?: string | number | null) =>
  `room:${roomId ?? 'NA'}|round:${round ?? 'NA'}`;

export const isStreamPath = (): boolean => {
  if (typeof window === 'undefined') return false; // SSR / tests
  // match "/stream/...", tolerant to trailing slashes
  return /(^|\/)stream(\/|$)/.test(window.location.pathname);
};
